/*
 * All the names of the events tracked in AppDir are defined here.
 * These names are then imported in fe-app-appdir (and fe-appdir-data-app),
 * which pass them down to the appropriate components via the gaTrackId(s) prop.
 * At the lowest level, this prop will add data-ga-track-id={props.gaTrackId} to <a> and <button> elements,
 * and Google Tag Manager will automatically log click events on these.
 *
 * FYI, another prop is sometimes used: gaTrackInfo. Its purpose is to pass down
 * _dynamic_ information, such as the app slug (used as a human-friendly id) or the search query.
 * The gaTrackInfo prop is not defined in this file because it is dynamic (only constants are defined here).
 * Note that in Google Analytics terms, gaTrackId corresponds to the event_name, and gaTrackInfo to the event_label.
 *
 * Finally, in some special cases (card focused, search input engaged/submitted, search result clicked)
 * we can't use the simple data-ga-track-id and data-ga-track-info approach described above.
 * Instead, we need to call the logCustomEvent function defined below with various extra data.
 * To keep things clean, parent components construct callback functions (like gaFocusedCallback) which call logCustomEvent;
 * these callbacks are passed down to the child component(s) that actually need to trigger the callback.
 */
const ViewTrackIds = {
  // Home page
  CURATED_COLLECTION: 'curated_collection',
  CURATED_COLLECTION_APP: 'curated_collection_app',
  COMPARE_PLANS: 'compare_plans',
  // DEVELOPERS_CTA: 'developers_cta',
  // REQUEST_DEMO: 'request_demo',

  // Categories page
  CATEGORY: 'category',

  // App page
  INSTALL_APP: 'install_app',
  // PLAY_VIDEO: 'play_video',
  // DOWNLOAD_USER_GUIDE: 'download_user_guide',

  // Shared events on Home, Categories, App page
  LOAD_MORE_APPS: 'load_more_apps',
  VIEW_ALL_APPS: 'view_all_apps',
};

const NavTrackIds = {
  NAV_HOME: 'nav_home',
  NAV_APPS: 'nav_apps',
  NAV_CATEGORIES: 'nav_categories',
  NAV_DASHBOARD: 'nav_dashboard',
  NAV_SIGN_UP: 'nav_sign_up',
  NAV_LOG_IN: 'nav_log_in',
  NAV_LOG_OUT: 'nav_log_out',
};

const SearchTrackIds = {
  SEARCH_ENGAGED: 'search_engaged',
  SEARCH_SUBMIT: 'search_submit',
  /* The following two events are both sent when a user clicks on a app in the search results,
   * but they track different information: 1st tracks the query, 2nd tracks the mapping query -> app slug */
  SEARCH_SELECT_DROPDOWN_QUERY: 'search_select_dropdown_query',
  SEARCH_SELECT_DROPDOWN_APP_BY_QUERY: 'search_select_dropdown_app_by_query',
};

const AppCardTrackIds = {
  FOCUS_APP: 'focus_app',
  INSTALL_APP: 'install_app',
  APP_DETAILS: 'app_details',
};

const ContactInfoTrackIds = {
  PROVIDER_WEBSITE: 'provider_website',
  PROVIDER_SUPPORT: 'provider_support',
  PARTNER_WEBSITE: 'partner_website',
  PARTNER_APP_DIRECTORY: 'partner_app_directory',
};

/**
 * Log a custom event to Google Tag manager
 *
 * @param {string} action - event name, normally passed as data-ga-track-id
 * @param {string} [label] - optional event label, normally passed as data-ga-track-info
 */
const logCustomEvent = (action, label) => {
  window.dataLayer.push({
    event: 'google_analytics_auto_tagging_event',
    event_category: 'App Directory',
    event_action: action,
    event_label: label,
    event_nonInteraction: '0',
  });
};

/**
 * Since we are using client-side routing, we must log page views manually.
 * This function is used as the render prop of an empty Route component in fe-appdir-app:
 * every time the location changes, the Route component re-renders, triggering the code below.
 */
const logVirtualPageView = () => {
  // During the very first render, the dataLayer is not available yet
  // The app re-renders right away though, so the first page view will be logged
  if (window.dataLayer)
    window.dataLayer.push({
      event: 'e_vpv',
    });

  // Must return null to indicate that this Route doesn't render anything
  return null;
};

export {
  ViewTrackIds,
  NavTrackIds,
  SearchTrackIds,
  AppCardTrackIds,
  ContactInfoTrackIds,
  logCustomEvent,
  logVirtualPageView,
};
